import { UserCircle } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import {
  EntityDropdownMenu,
  Field,
  FieldLayout,
  hasConflicts,
  LinkToEntity,
  renderBadge,
} from "@/src/components/cards/common";
import PatientCardEditable from "@/src/components/cards/patient-card/patient-card-editable";
import ErrorAlert from "@/src/components/error-alert";
import { Card, CardTitle, CardHeader, CardContent } from "@/src/components/shadcn/ui/card";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { useUser } from "@/src/context/user-context";
import { logger } from "@/src/lib/logging/logger";
import { canAccessInbox, canWritePatient } from "@/src/lib/rbac";
import { tryGetEhrSystemId, tryGetMrn } from "@/src/lib/resolve-ehr-ids";
import { Patient } from "@/src/models/patient";

export const PATIENT_FIELDS: Field[] = [
  { key: "familyName", label: "Family Name", comparable: true, required: true },
  { key: "givenName", label: "Given Name", comparable: true, required: true },
  { key: "middleName", label: "Middle Name", comparable: true },
  { key: "dateOfBirth", label: "Date of Birth", type: "date", comparable: true },
  { key: "gender", label: "Gender", comparable: true },
  { key: "race", label: "Race", comparable: false },
  { key: "ethnicity", label: "Ethnicity", comparable: false },
  { key: "language", label: "Language", comparable: false },
  {
    key: "ehrId",
    label: "EHR ID",
    comparable: false,
    getValue: (p: Patient) => tryGetMrn(p)?.replace(/^0+/, "") || tryGetEhrSystemId(p),
  },
  { key: "homeAddress.line1", label: "Address", comparable: true, colSpan: 2, sectionHeader: "Address" },
  { key: "homeAddress.line2", label: "Address 2", comparable: true },
  { key: "homeAddress.city", label: "City", comparable: true },
  { key: "homeAddress.state", label: "State", comparable: true },
  { key: "homeAddress.postalCode", label: "Zip Code", comparable: true },
  { key: "homePhoneNumber", label: "Home Phone", type: "tel", comparable: true },
  { key: "mobilePhoneNumber", label: "Mobile Phone", type: "tel", comparable: true },
  { key: "personalEmail", label: "Personal Email", comparable: true },
  { key: "type", label: "Type", comparable: false },
];

export default function PatientCard({
  patient,
  compareMode = false,
  secondaryPatient: secondaryPatient = null,
}: {
  patient: Patient;
  onClick?: () => void;
  compareMode?: boolean;
  secondaryPatient?: Patient | null;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { group, roles } = useUser();
  const canWrite = canWritePatient(roles);
  const canContact = canAccessInbox(roles);

  const fields = useMemo<Field[]>(
    () =>
      PATIENT_FIELDS.map((f) => {
        if (f.key !== "ehrId") return f;

        return {
          ...f,
          getValue: (p: Patient) => tryGetMrn(p, group)?.replace(/^0+/, "") || tryGetEhrSystemId(p, group),
        };
      }),
    [group]
  );

  useEffect(() => {
    logger.info("Patient in patient card", { patient, secondaryPatient });
  }, [patient, secondaryPatient]);

  let hasAnyConflicts = false;

  if (compareMode && secondaryPatient) {
    hasAnyConflicts = hasConflicts(fields, patient, secondaryPatient);
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  if (isEditing) {
    return (
      <PatientCardEditable
        patient={patient}
        onClose={handleEditToggle}
        compareMode={compareMode}
        secondaryObject={secondaryPatient}
        fields={fields}
      />
    );
  }

  const cardActionsProps = createCardActionsProps({
    id: patient.id,
    label: "Patient ID",
    href: `/patients/${patient.displayId}`,
    customProps: [{ label: "Edit Patient", onClick: handleEditToggle }],
  });

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <Card className={`w-full focus:outline-none focus:ring-0`} tabIndex={0}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <UserCircle className="h-6 w-6 mr-2" />
                <CardTitle className="text-md font-semibold">Patient Information</CardTitle>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {patient && renderBadge(compareMode, secondaryPatient, hasAnyConflicts)}
                {patient && !pathname?.startsWith(`/patients/${patient.displayId}`) ? (
                  <LinkToEntity href={`/patients/${patient.displayId}`} />
                ) : null}
                {canWrite && patient && (
                  <EntityDropdownMenu
                    cardActionsProps={cardActionsProps}
                    customProps={
                      canContact
                        ? [{ label: "Contact Patient", onClick: () => router.push(`/inbox/${patient.displayId}`) }]
                        : []
                    }
                  />
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!patient ? (
              <ErrorAlert message="Patient not found" compact />
            ) : (
              <FieldLayout
                fields={fields}
                patient={patient}
                secondaryObject={secondaryPatient}
                compareMode={compareMode}
              />
            )}
          </CardContent>
        </Card>
      </ContextMenuTrigger>
      {canWrite && patient && <CardActions {...cardActionsProps} isContextMenu={true} />}
    </ContextMenu>
  );
}

function GridItemSkeleton() {
  return <Skeleton className="inline-block align-text-bottom h-4 w-[100px]" />;
}

export function PatientCardSkeleton() {
  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center">
          <UserCircle className="h-6 w-6 mr-2" />
          <CardTitle className="text-md font-semibold">Patient Information</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div>
            <span className="font-semibold">Last Name:</span> <GridItemSkeleton />
          </div>
          <div>
            <span className="font-semibold">Given Name:</span> <GridItemSkeleton />
          </div>
          <div>
            <span className="font-semibold">Date of Birth:</span> <GridItemSkeleton />
          </div>
          <div>
            <span className="font-semibold">Gender:</span> <GridItemSkeleton />
          </div>
          <div>
            <span className="font-semibold">Email:</span> <GridItemSkeleton />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
